// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/pages/ReceiptPackageMatching.jsx

import React, { useEffect, useState, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useParams } from 'react-router-dom'; // เพื่อรับ userId จาก URL

import Sidebar from './Sidebar';
import Header from '../components/Header';
import PropTypes from 'prop-types';
import '../css/ReceiptPackageMatching.scss'; // ใช้ SCSS ที่สร้างใหม่
import { API_URLS, getSelectedLanguage } from '../api/api';
import { Button } from 'primereact/button';

const ReceiptPackageMatching = () => {
  const { userId } = useParams(); // รับ userId จาก URL
  const [language] = useState(getSelectedLanguage().toUpperCase());
  const [translations, setTranslations] = useState({});
  const [fixedRate, setFixedRate] = useState({});
  const [storeConstants, setStoreConstants] = useState({});
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);

  const receiptRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => receiptRef.current,
    documentTitle: 'Receipt-PackageMatching',
    onBeforeGetContent: () => setLoading(true),
    onAfterPrint: () => setLoading(false),
  });

  // โหลดข้อมูลร้านค้า (store constants)
  useEffect(() => {
    const fetchBranchDetails = async () => {
      try {
        let userID = localStorage.getItem('user_ID');
        if (!userID) {
          // ในตัวอย่างนี้สมมติว่าไม่มี API fetchSomeUserAPI
          throw new Error('User ID not found in localStorage');
        }

        const branchResponse = await fetch(API_URLS.BRANCH_DETAILS(userID));
        if (!branchResponse.ok) {
          throw new Error(`Failed to fetch branch details with status: ${branchResponse.status}`);
        }

        const branchData = await branchResponse.json();
        if (branchData.length > 0) {
          const branchInfo = branchData[0];
          setStoreConstants({
            name: branchInfo.branch_company,
            address: branchInfo.location,
            notes: branchInfo.branch_name,
            phone: branchInfo.phone,
            email: branchInfo.branch_email,
            img: branchInfo.branch_img,
          });
        }
      } catch (error) {
        console.error("Fetching branch details failed:", error);
      }
    };

    fetchBranchDetails();
  }, []);

  // โหลดข้อมูลการแปลและอัตราภาษี
  useEffect(() => {
    const fetchConfigData = async () => {
      try {
        const translationsResp = await fetch(API_URLS.TRANSLATIONS);
        const translationsData = await translationsResp.json();
        setTranslations(translationsData);

        const fixedRateResp = await fetch(API_URLS.FIXED_RATE);
        const fixedRateData = await fixedRateResp.json();
        setFixedRate(fixedRateData);

        const storeConstantsResp = await fetch(API_URLS.STORE_CONSTANTS);
        const storeConstantsData = await storeConstantsResp.json();
        setStoreConstants(storeConstantsData);
      } catch (error) {
        console.error("Fetching config data failed:", error);
      }
    };
    fetchConfigData();
  }, []);

  // โหลดข้อมูลจาก API SPECIFIC_USER
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) {
        console.error("No userId provided");
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found. Please log in.');
        }

        const response = await fetch(API_URLS.SPECIFIC_USER(userId), {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.length > 0) {
          setUserData(data[0]);
          // คำนวณ totalAmount จาก user_packages
          const packages = data[0].user_packages || [];
          const total = packages.reduce((sum, pkg) => sum + parseFloat(pkg.price || 0), 0);
          setTotalAmount(total);
        }
      } catch (error) {
        console.error("Fetching user data failed:", error);
      }
    };

    fetchUserData();
  }, [userId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const t = translations[language] || {};

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="dashboard-main">
        <Header refreshData={() => {}} />

        <Button
          label="Download PDF"
          icon="pi pi-file-pdf"
          className="print-button"
          loading={loading}
          onClick={handlePrint}
        />

        <div className="content-area">
          <div ref={receiptRef} className="invoice-container">
            {/* Header Section */}
            <header className="invoice-header">
              <img src={storeConstants?.img} alt="invoice Logo" className="invoice-logo" />
              <h1>{t['RECEIPT']}</h1>
            </header>

            {/* Client Details Section */}
            <section className="client-details">
              <div className="text-left">
                <strong>{t['RECEIPT_TO']}</strong>
                <p>{userData?.profile_data?.[0]?.name} {userData?.profile_data?.[0]?.sur_name}</p>

                <div className="line-index"></div>

                <strong>{t['CONTACT_PERSON']}</strong>
                <p className="bottom-unset">{t['PHONE']}: {userData?.profile_data?.[0]?.telephone || 'N/A'}</p>
                <p className="bottom-unset">{t['EMAIL']}: {userData?.profile_data?.[0]?.email || 'N/A'}</p>
                <p>{t['ADDRESS']}: {userData?.profile_data?.[0]?.location || 'N/A'}</p>
              </div>

              <div className="text-left">
                <p className="bottom-unset">{t['RECEIPT_NO']}: {userData?.user_packages?.[0]?.package_id || 'N/A'}</p>
                <p>{t['RECEIPT_DATE']}: {formatDate(userData?.user_packages?.[0]?.date)}</p>

                <div className="line-index"></div>
                <strong>{t['PAYMENT_METHOD']}</strong>
                <p className="bottom-unset">{t['ORDER_ID']}: {userData?.user_packages?.[0]?.package_id || 'N/A'}</p>
                <p className="bottom-unset">{t['ACCOUNT_NAME']}: {userData?.profile_data?.[0]?.name} {userData?.profile_data?.[0]?.sur_name}</p>
                <p>{t['STATUS']}: {userData?.user_packages?.[0]?.status_name || 'N/A'}</p>
              </div>
            </section>

            {/* Receipt Items Section */}
            <table className="invoice-items">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t['DESCRIPTION']}</th>
                  <th>{t['QUANTITY']}</th>
                  <th>{t['SERVICE']}</th>
                  <th>{t['PRICE']}</th>
                </tr>
              </thead>
              <tbody>
                {(userData?.user_packages || []).map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.package || 'N/A'}</td>
                    <td>{`${formatDate(item.date)} ${item.time}`}</td>
                    <td>{item.status_name || 'N/A'}</td>
                    <td>{item.price ? `${item.price} $` : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="row mt-4">
              <div className="col-6">
                <img src={storeConstants?.img} alt="invoice Logo" className="footer-logo" />
                <p className="bottom-unset">{storeConstants?.name}</p>
                <p>{storeConstants?.address}</p>
                <div className="line-index"></div>
                <p>{storeConstants?.notes}</p>
              </div>

              <div className="col-3">
                <p className="grid-item sub-total-label">{t['SUB_TOTAL']}</p>
                <p className="grid-item sub-total-label">{t['TAX']} ({fixedRate.tax}%)</p>
                <p className="grid-item sub-total-label">{t['TOTAL_TRA']}</p>
              </div>

              <div className="col-3">
                <p className="grid-item">{totalAmount.toFixed(2)}</p>
                <p className="grid-item">{((totalAmount * (fixedRate.tax || 0)) / 100).toFixed(2)}</p>
                <p className="grid-item">{(totalAmount + (totalAmount * (fixedRate.tax || 0)) / 100).toFixed(2)}</p>
              </div>
            </div>

            <div className="line-index"></div>
            <div className="mt-2">
              <div className="legal-notes text-left">
                <strong>{t['NOTES_IMPORTANTES']} :</strong>
                <p>{t['NOTES_IMPORTANT']}</p>
              </div>

              <div className="row mt-4">
                <div className="col-5">
                  <p className="icon-text">
                    <i className="pi pi-map-marker large-icon"></i>
                    <span>{storeConstants.address}</span>
                  </p>
                </div>

                <div className="col-3">
                  <p className="icon-text">
                    <i className="pi pi-phone large-icon"></i>
                    <span>{storeConstants.phone}</span>
                  </p>
                </div>

                <div className="col-4">
                  <p className="icon-text">
                    <i className="pi pi-envelope large-icon"></i>
                    <span>{storeConstants.email}</span>
                  </p>
                </div>
              </div>

              <div className="line-index"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ReceiptPackageMatching.propTypes = {
  language: PropTypes.string,
};

export default ReceiptPackageMatching;